import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { PdfData } from './pdfGenerator';

// Define the assets interface
export interface PdfAssets {
  logoUrl: string;
  markUrl: string;
  architectUrl: string;
}

/**
 * Creates a document definition for the architecture report PDF
 * @param data Project metadata for the PDF
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @param assets Logo and font assets for the PDF
 * @returns PDF document definition
 */
export const createArchitectureReportTemplate = (
  data: PdfData,
  diagramImageBase64?: string,
  assets?: PdfAssets
): TDocumentDefinitions => {
  // Format the date for display
  const formattedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Create the document definition
  return {
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 60],
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.5,
      font: 'IBMPlexSans'
    },
    info: {
      title: `${data.title} - Architecture Report`,
      author: data.author,
      subject: 'Data Architecture Design',
      keywords: 'MetaRouter, Architecture, Data Flow'
    },
    header: function(currentPage: number) {
      // Only show the logo on the first page
      if (currentPage === 1 && assets?.logoUrl) {
        return { text: '', margin: [0, 0, 0, 0] };
      } else {
        return { text: 'MetaRouter Architect', fontSize: 18, bold: true, margin: [40, 20, 0, 0] };
      }
    },
    footer: (currentPage: number, pageCount: number) => {
      return {
        columns: [
          { 
            text: 'MetaRouter Architect - Data Flow Design Tool', 
            alignment: 'left', 
            margin: [40, 0, 0, 0] 
          },
          { 
            text: `Page ${currentPage} of ${pageCount}`, 
            alignment: 'right', 
            margin: [0, 0, 40, 0] 
          }
        ],
        margin: [40, 20],
        fontSize: 8,
        color: '#161322'
      };
    },
    content: [
      // Title Section
      {
        stack: [
          { text: 'Architecture Report', fontSize: 10, color: '#161322', margin: [0, 0, 0, 5] },
          { text: data.title, fontSize: 24, style: 'header', margin: [0, 0, 0, 10] },
          { text: data.description, fontSize: 12, color: '#525365', margin: [0, 0, 0, 20] },
          { text: `Generated on ${formattedDate}`, fontSize: 10, color: '#9294aa', margin: [0, 0, 0, 30] }
        ],
        margin: [0, 40, 0, 20]
      },

      // Diagram Section
      {
        stack: [
          { text: 'Data Flow Diagram', fontSize: 16, style: 'subheader', margin: [0, 0, 0, 10] },
          diagramImageBase64 ? {
            image: diagramImageBase64.startsWith('data:') ? diagramImageBase64 : `data:image/png;base64,${diagramImageBase64}`,
            width: 515,
            height: 300,
            alignment: 'center',
            margin: [0, 0, 0, 20]
          } : {
            // Placeholder rectangle if no image is provided
            canvas: [
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 515,
                h: 300,
                color: '#cccccc',
                lineWidth: 1,
                lineColor: '#cccccc'
              },
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 300,
                lineWidth: 1,
                lineColor: '#cccccc'
              },
              {
                type: 'line',
                x1: 515,
                y1: 0,
                x2: 0,
                y2: 300,
                lineWidth: 1,
                lineColor: '#cccccc'
              }
            ],
            margin: [0, 0, 0, 20]
          }
        ],
        margin: [0, 0, 0, 30]
      },

      // Project Details Section
      {
        stack: [
          { text: 'Project Details', fontSize: 16, style: 'subheader', margin: [0, 0, 0, 10] },
          {
            table: {
              headerRows: 0,
              widths: ['30%', '70%'],
              body: [
                [{ text: 'Project ID', style: 'boldText' }, data.projectId],
                [{ text: 'Created By', style: 'boldText' }, data.author],
                [{ text: 'Created On', style: 'boldText' }, data.createdAt],
                [{ text: 'Total Nodes', style: 'boldText' }, data.nodeCount.toString()],
                [{ text: 'Total Connections', style: 'boldText' }, data.edgeCount.toString()]
              ]
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#cccccc',
              vLineColor: () => '#cccccc',
              paddingLeft: () => 10,
              paddingRight: () => 10,
              paddingTop: () => 5,
              paddingBottom: () => 5
            },
            margin: [0, 0, 0, 20]
          }
        ],
        margin: [0, 0, 0, 30]
      },

      // Sources and Destinations Section
      {
        columns: [
          {
            stack: [
              { text: 'Sources', fontSize: 14, style: 'subheader', margin: [0, 0, 0, 10] },
              {
                ul: data.sources.length > 0 ? data.sources : ['No sources defined'],
                margin: [0, 0, 0, 0]
              }
            ],
            width: '50%'
          },
          {
            stack: [
              { text: 'Destinations', fontSize: 14, style: 'subheader', margin: [0, 0, 0, 10] },
              {
                ul: data.destinations.length > 0 ? data.destinations : ['No destinations defined'],
                margin: [0, 0, 0, 0]
              }
            ],
            width: '50%'
          }
        ],
        margin: [0, 0, 0, 30]
      },

      // Summary Section
      {
        stack: [
          { text: 'Summary', fontSize: 16, style: 'subheader', margin: [0, 0, 0, 10] },
          { text: 'This report provides an overview of the data architecture design created using MetaRouter Architect. The diagram illustrates the flow of data from sources through various processing nodes to destinations.', margin: [0, 0, 0, 10] },
          { text: 'For more details or to modify this architecture, please visit the MetaRouter Architect application.', margin: [0, 0, 0, 0] }
        ],
        margin: [0, 0, 0, 0]
      }
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10]
      },
      subheader: {
        fontSize: 14,
        bold: true,
        margin: [0, 10, 0, 5]
      },
      boldText: {
        bold: true
      }
    }
  };
};
